import { z } from 'zod';

const VISITOR_KEY = 'affideaReferralVisitor';
const VISIT_KEY = 'affideaReferralVisit';
const ATTRIBUTION_MS = 24 * 60 * 60 * 1000;
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
let visitorId;
let currentVisit;

const newId = () => {
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10).join('')}`;
};

export const createReferralVisitRequest = () => {
  try {
    visitorId = window.localStorage.getItem(VISITOR_KEY) || visitorId;
  } catch {
    // In-memory attribution still works when browser storage is blocked.
  }
  if (!GUID.test(visitorId || '')) visitorId = newId();
  try {
    window.localStorage.setItem(VISITOR_KEY, visitorId);
  } catch {}
  return { visitId: newId(), visitorId };
};

export const clearReferralVisit = () => {
  currentVisit = null;
  try {
    window.sessionStorage.removeItem(VISIT_KEY);
  } catch {}
};

const saveVisit = (visit) => {
  currentVisit = visit;
  try {
    window.sessionStorage.setItem(VISIT_KEY, JSON.stringify(currentVisit));
  } catch {}
};

// Keep booking preferences even when recording the anonymous visit fails.
export const rememberKlinikaDoBooking = (email) => {
  const parsed = z.string().trim().max(254).email().safeParse(email);
  saveVisit({
    visitId: null,
    email: parsed.success ? parsed.data : '',
    createdAt: Date.now(),
  });
};

export const rememberReferralVisit = (visitId) => {
  if (!GUID.test(visitId || '')) return;
  saveVisit({ ...readReferralVisit(), visitId, createdAt: Date.now() });
};

const readReferralVisit = () => {
  let visit = currentVisit;
  try {
    const saved = window.sessionStorage.getItem(VISIT_KEY);
    if (saved) visit = JSON.parse(saved);
  } catch {}
  if (!visit || (visit.visitId !== null && !GUID.test(visit.visitId || '')) ||
      !Number.isFinite(visit.createdAt) || visit.createdAt > Date.now() ||
      Date.now() - visit.createdAt >= ATTRIBUTION_MS) {
    clearReferralVisit();
    return null;
  }
  return visit;
};

export const readKlinikaDoBooking = () => {
  const visit = readReferralVisit();
  return visit?.bookingEnded ? null : visit;
};

export const allowKlinikaDoAdditionalLabTests = () => {
  const visit = readKlinikaDoBooking();
  if (visit) saveVisit({ ...visit, additionalLabTestsAllowed: true });
};

export const endKlinikaDoBooking = () => {
  const visit = readKlinikaDoBooking();
  // Retain anonymous referral attribution separately from booking preferences.
  if (visit) saveVisit({ ...visit, email: '', bookingEnded: true });
};

export const readReferralVisitId = () => readReferralVisit()?.visitId ?? null;
